import { BALANCE } from '../config/balance';
import { clamp } from '../utils/math';

/**
 * The player's physical setup: screen size and how far they sit from it.
 *
 * The off-axis window is only as convincing as the numbers behind it, so this
 * is what turns the normalized head position into real metres of movement and
 * the virtual screen into something at the player's own distance. The maths is
 * pure and unit-tested; persistence and the wizard live elsewhere.
 *
 * Nothing here changes the shipped behaviour until the player completes the
 * wizard: `calibrated` defaults to false, and the uncalibrated path keeps the
 * fixed `BALANCE` constants exactly as they were.
 */
export interface ScreenCalibrationData {
  /** Visible screen width, centimetres (bezels excluded). */
  screenWidthCm: number;
  /** Visible screen height, centimetres. */
  screenHeightCm: number;
  /** Distance from the eyes to the screen, centimetres. */
  viewingDistanceCm: number;
  /** True once the player has been through the wizard. */
  calibrated: boolean;
}

/** The values the game shipped with, so defaults mean "as before". */
export const DEFAULT_SCREEN_CALIBRATION: ScreenCalibrationData = {
  screenWidthCm: 34,
  screenHeightCm: 19,
  viewingDistanceCm: 60,
  calibrated: false,
};

const MIN_WIDTH_CM = 15;
const MAX_WIDTH_CM = 300;
const MIN_HEIGHT_CM = 10;
const MAX_HEIGHT_CM = 200;
const MIN_DISTANCE_CM = 25;
const MAX_DISTANCE_CM = 200;

/** Clamp a possibly hand-edited calibration into a sane physical range. */
export function sanitizeScreenCalibration(
  input: Partial<ScreenCalibrationData> | null | undefined,
): ScreenCalibrationData {
  const width = clamp(
    finite(input?.screenWidthCm, DEFAULT_SCREEN_CALIBRATION.screenWidthCm),
    MIN_WIDTH_CM,
    MAX_WIDTH_CM,
  );
  const height = clamp(
    finite(input?.screenHeightCm, DEFAULT_SCREEN_CALIBRATION.screenHeightCm),
    MIN_HEIGHT_CM,
    MAX_HEIGHT_CM,
  );
  const distance = clamp(
    finite(input?.viewingDistanceCm, DEFAULT_SCREEN_CALIBRATION.viewingDistanceCm),
    MIN_DISTANCE_CM,
    MAX_DISTANCE_CM,
  );
  return {
    screenWidthCm: width,
    screenHeightCm: height,
    viewingDistanceCm: distance,
    calibrated: input?.calibrated === true,
  };
}

function finite(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function cmToInches(cm: number): number {
  return cm / 2.54;
}

export function inchesToCm(inches: number): number {
  return inches * 2.54;
}

/** Screen aspect ratio, width over height. */
export function screenAspect(data: ScreenCalibrationData): number {
  return data.screenWidthCm / data.screenHeightCm;
}

/** The screen in world metres (the projection's world scale is 1 unit = 1 m). */
export function screenWorldMetres(data: ScreenCalibrationData): {
  width: number;
  height: number;
} {
  return { width: data.screenWidthCm / 100, height: data.screenHeightCm / 100 };
}

/**
 * How far the virtual screen sits from the viewer, metres.
 *
 * Clamped to the closest the window is allowed to get, so a stored distance of
 * a few centimetres cannot put the near plane behind the eye.
 */
export function viewingDistanceMetres(data: ScreenCalibrationData): number {
  return Math.max(BALANCE.camera.offAxis.minScreenDistance, data.viewingDistanceCm / 100);
}

/**
 * Normalized iris offset -> real metres, from the calibrated screen.
 *
 * A head crossing the whole camera frame spans `screenMovementScale` screen
 * widths (height for the vertical channel, kept at half strength like the fixed
 * scale it replaces, so a nod still reads gentler than a lean).
 */
export function calibratedViewScale(data: ScreenCalibrationData): { x: number; y: number } {
  const world = screenWorldMetres(data);
  const movement = BALANCE.headTracking.screenMovementScale;
  return { x: world.width * movement, y: (world.height * movement) / 2 };
}

/** The viewer's usable travel, metres, and the body-sway dead zone inside it. */
export interface ViewerReach {
  x: number;
  y: number;
  forward: number;
  deadZone: number;
}

/** The reach the uncalibrated path uses: the shipped absolute clamps. */
export function defaultViewerReach(): ViewerReach {
  const off = BALANCE.camera.offAxis;
  return {
    x: off.maxViewerX,
    y: off.maxViewerY,
    forward: off.maxViewerForward,
    deadZone: BALANCE.headTracking.bodyShiftDeadZone,
  };
}

/**
 * The viewer's usable travel from the calibration, metres.
 *
 * Real-metre head offsets are far smaller than the shipped absolute clamps, so
 * the reach is rebuilt from the scale: a full-frame head travel times the
 * documented fractions. The window clamp, the body-dodge normalisation and the
 * head-map dead-zone box all read this, so the feel is the same *fraction of a
 * lean* whether or not the player has calibrated.
 */
export function calibratedReach(data: ScreenCalibrationData): ViewerReach {
  const scale = calibratedViewScale(data);
  const tuning = BALANCE.headTracking;
  return {
    x: scale.x * tuning.screenReachFraction,
    y: scale.y * tuning.screenReachFraction,
    forward: scale.x * tuning.screenReachForwardFraction,
    deadZone: scale.x * tuning.screenReachDeadFraction,
  };
}

/** The reach for a calibration: calibrated when it has one, shipped otherwise. */
export function viewerReachFor(data: ScreenCalibrationData): ViewerReach {
  return data.calibrated ? calibratedReach(data) : defaultViewerReach();
}

/**
 * Estimate the viewing distance from the measured iris separation.
 *
 * A pinhole estimate against a webcam reference: separation is inversely
 * proportional to distance, so the shipped reference point scales it. It is a
 * starting value for the wizard — the manual field is the source of truth.
 */
export function estimateDistanceCm(separation: number): number | null {
  if (!(separation > 1e-4)) return null;
  const reference = BALANCE.headTracking;
  const distance =
    (reference.distanceReferenceCm * reference.distanceReferenceSeparation) / separation;
  return clamp(distance, MIN_DISTANCE_CM, MAX_DISTANCE_CM);
}
